import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Users, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE, DRIVER_STATUS } from "@/lib/util";
import { Loader, Pill, Field, VehNo } from "@/components/common";
import Modal from "@/components/Modal";
import { PageTitle, SearchBox, Table, Td, useDebounced } from "@/pages/admin/ui";

export const WorkTypes = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-2">
    {[[["tow"], "ونش"], [["lorry"], "هاف لوري"], [["tow", "lorry"], "كلاهما"]].map(([v, l]) => {
      const on = JSON.stringify([...value].sort()) === JSON.stringify([...v].sort());
      return <button type="button" key={l} data-testid={`work-type-${v.join("-")}`} onClick={() => onChange(v)}
        className={`h-12 rounded-xl border-2 font-bold ${on ? "border-blue-700 bg-blue-50 text-blue-800" : "border-slate-200"}`}>{l}</button>;
    })}
  </div>
);

function NewDriver({ open, onClose, vehicles }) {
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", phone: "", password: "", work_types: ["tow"], civil_id: "", nationality: "", vehicle_id: "" });
  const [mode, setMode] = useState("new");
  const [nv, setNv] = useState({ admin_number: "", plate: "", type: "", make: "", model: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (mode === "existing" && !f.vehicle_id) return toast.error("اختر مركبة السائق");
    if (mode === "new" && (!nv.admin_number.trim() || !nv.plate.trim())) return toast.error("أدخل الرقم الإداري والرقم العادي للمركبة");
    setBusy(true);
    try {
      const body = mode === "existing" ? { ...f } : { ...f, vehicle_id: null, new_vehicle: { ...nv, type: nv.type || f.work_types[0] } };
      const { data } = await api.post("/drivers", body);
      toast.success(`تمت إضافة السائق برقم ${data.serial}`);
      nav(`/admin/drivers/${data.id}`);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title="إضافة سائق جديد" testid="new-driver-modal">
      <form onSubmit={save} className="space-y-3">
        <Field label="الاسم الكامل" required><input data-testid="nd-name" className="field" value={f.full_name} onChange={set("full_name")} required /></Field>
        <Field label="رقم الهاتف (للدخول)" required><input data-testid="nd-phone" className="field ltr text-left" inputMode="tel" value={f.phone} onChange={set("phone")} required /></Field>
        <Field label="كلمة المرور المؤقتة" required hint="6 أحرف على الأقل، يستطيع السائق تغييرها لاحقاً"><input data-testid="nd-password" className="field" value={f.password} onChange={set("password")} required /></Field>
        <Field label="نوع العمل" required><WorkTypes value={f.work_types} onChange={(v) => setF({ ...f, work_types: v })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="الرقم المدني"><input data-testid="nd-civil" className="field" value={f.civil_id} onChange={set("civil_id")} /></Field>
          <Field label="الجنسية"><input data-testid="nd-nationality" className="field" value={f.nationality} onChange={set("nationality")} /></Field>
        </div>
        <div className="rounded-2xl border-2 border-blue-100 bg-blue-50/40 p-3 space-y-3">
          <div className="font-bold">مركبة السائق <span className="text-red-600">*</span></div>
          <div className="grid grid-cols-2 gap-2">
            {[["new", "مركبة جديدة"], ["existing", "مركبة مسجلة"]].map(([k, l]) => (
              <button type="button" key={k} data-testid={`nd-vehicle-mode-${k}`} onClick={() => setMode(k)}
                className={`h-11 rounded-xl border-2 font-bold ${mode === k ? "border-blue-700 bg-white text-blue-800" : "border-slate-200"}`}>{l}</button>))}
          </div>
          {mode === "new" ? (<>
            <div className="grid grid-cols-2 gap-3">
              <Field label="الرقم الإداري (رقمان)" required><input data-testid="nd-veh-admin" className="field field-sm ltr text-left" inputMode="numeric" maxLength={2} value={nv.admin_number} onChange={(e) => setNv({ ...nv, admin_number: e.target.value.replace(/\D/g, "").slice(0, 2) })} /></Field>
              <Field label="الرقم العادي (اللوحة)" required><input data-testid="nd-veh-plate" className="field field-sm ltr text-left" value={nv.plate} onChange={(e) => setNv({ ...nv, plate: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="النوع"><select data-testid="nd-veh-type" className="field field-sm" value={nv.type || f.work_types[0]} onChange={(e) => setNv({ ...nv, type: e.target.value })}>
                <option value="tow">ونش</option><option value="lorry">هاف لوري</option></select></Field>
              <Field label="الماركة"><input className="field field-sm" value={nv.make} onChange={(e) => setNv({ ...nv, make: e.target.value })} /></Field>
              <Field label="الموديل"><input className="field field-sm" value={nv.model} onChange={(e) => setNv({ ...nv, model: e.target.value })} /></Field>
            </div>
          </>) : (
            <select data-testid="nd-vehicle" className="field" value={f.vehicle_id} onChange={set("vehicle_id")}>
              <option value="">اختر المركبة</option>
              {vehicles.filter((v) => !v.current_driver_id).map((v) => <option key={v.id} value={v.id}>{[v.admin_number, v.plate].filter(Boolean).join("/")} — {SERVICE[v.type]}</option>)}
            </select>
          )}
        </div>
        <button data-testid="nd-submit" disabled={busy} className="btn btn-lg btn-blue w-full">حفظ السائق</button>
      </form>
    </Modal>
  );
}

export default function Drivers() {
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(sp.get("status") || "");
  const [wt, setWt] = useState("");
  const dq = useDebounced(q);
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [add, setAdd] = useState(false);
  useEffect(() => {
    api.get("/drivers", { params: { q: dq, status, work_type: wt } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq, status, wt]);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PageTitle title="ملفات السائقين" sub={data ? `${data.total} سائق` : ""} icon={Users}>
        {can("drivers_manage") && <button data-testid="add-driver-btn" onClick={() => setAdd(true)} className="btn btn-md btn-blue"><UserPlus className="w-5 h-5" />إضافة سائق</button>}
      </PageTitle>
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={setQ} placeholder="ابحث بالاسم أو الهاتف أو الرقم التسلسلي أو الرقم الإداري/العادي للمركبة" testid="drivers-search" />
        <select data-testid="drivers-status-filter" className="field field-sm w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">كل الحالات</option>{Object.entries(DRIVER_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="drivers-type-filter" className="field field-sm w-40" value={wt} onChange={(e) => setWt(e.target.value)}>
          <option value="">كل الأنواع</option><option value="tow">ونش</option><option value="lorry">هاف لوري</option>
        </select>
      </div>
      {!data ? <Loader /> : (
        <Table head={["الرقم", "الاسم", "الهاتف", "نوع العمل", "المركبة", "الحالة"]} testid="drivers-table">
          {data.items.map((d) => (
            <tr key={d.id} className="hover:bg-slate-50">
              <Td><Link data-testid={`driver-row-${d.serial}`} to={`/admin/drivers/${d.id}`} className="ltr font-black text-blue-700">{d.serial}</Link></Td>
              <Td><Link to={`/admin/drivers/${d.id}`} className="font-bold">{d.full_name}</Link></Td>
              <Td><span className="ltr">{d.phone}</span></Td>
              <Td>{d.work_types.map((w) => SERVICE[w]).join(" + ")}</Td>
              <Td><VehNo adm={d.vehicle_admin_number} plate={d.vehicle_plate} /></Td>
              <Td><Pill color={d.status === "active" ? "green" : d.status === "suspended" ? "red" : "slate"}>{DRIVER_STATUS[d.status]}</Pill></Td>
            </tr>
          ))}
        </Table>
      )}
      <NewDriver open={add} onClose={() => setAdd(false)} vehicles={vehicles} />
    </div>
  );
}
